import { useState, useEffect } from 'react'
import { ArchiveBoxIcon, ViewColumnsIcon } from '@heroicons/react/24/outline'
import type { SalaryDisplayFormat, Currency, UserSettings, SubscriptionPlan } from '../types'
import { ProBadge } from './UpgradePrompt'
import { CURRENCY_SYMBOLS, SALARY_FORMAT_LABELS, SUPPORTED_CURRENCIES } from '../utils/currency'

type Props = {
  settings: UserSettings
  onSettingsChange: (settings: UserSettings) => void
  plan: SubscriptionPlan
  displayMode: 'cards' | 'table'
  hasApplications: boolean
  bulkEditMode: boolean
  onToggleBulkEdit: () => void
  onNavigateToPricing: () => void
}

export const ApplicationsToolbar = ({
  settings,
  onSettingsChange,
  plan,
  displayMode,
  hasApplications,
  bulkEditMode,
  onToggleBulkEdit,
  onNavigateToPricing,
}: Props) => {
  const isPro = plan === 'pro'
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)

  // Close column selector dropdown on outside click or escape key
  useEffect(() => {
    if (!isColumnSelectorOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-column-selector="true"]')) {
        setIsColumnSelectorOpen(false)
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsColumnSelectorOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isColumnSelectorOpen])

  return (
    <div className="relative flex flex-wrap items-center gap-3 rounded-lg border border-app-line bg-app-subtle px-3 py-2">
      {/* Pro badge protruding from top of main border */}
      {!isPro && (
        <div className="absolute -top-2.5 left-4 z-10">
          <ProBadge onClick={onNavigateToPricing} source="dashboard" feature="salary_settings" />
        </div>
      )}

      <span className="text-xs font-medium text-app-muted">
        Salary:
      </span>

      {/* Salary format */}
      <select
        value={settings.salaryDisplayFormat}
        onChange={(e) => {
          const newSettings = { ...settings, salaryDisplayFormat: e.target.value as SalaryDisplayFormat }
          onSettingsChange(newSettings)
        }}
        disabled={!isPro}
        className="border border-app-line bg-app-surface px-2 py-1 text-xs text-app-ink focus:border-app-accent focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
      >
        {(Object.keys(SALARY_FORMAT_LABELS) as SalaryDisplayFormat[]).map((format) => (
          <option key={format} value={format}>
            {SALARY_FORMAT_LABELS[format]}
          </option>
        ))}
      </select>

      <div className="h-4 w-px bg-app-line" />

      {/* Currency dropdown */}
      <span className="text-xs font-medium text-app-muted">Currency:</span>
      <select
        value={settings.targetCurrency}
        onChange={(e) => {
          const newCurrency = e.target.value as Currency
          const newSettings = {
            ...settings,
            targetCurrency: newCurrency,
            currencyConversionEnabled: newCurrency !== 'original'
          }
          onSettingsChange(newSettings)
        }}
        disabled={!isPro}
        className="border border-app-line bg-app-surface px-2 py-1 text-xs text-app-ink focus:border-app-accent focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
      >
        <option value="original">as posted</option>
        {SUPPORTED_CURRENCIES.map((currency) => (
          <option key={currency} value={currency}>
            {currency} ({CURRENCY_SYMBOLS[currency]})
          </option>
        ))}
      </select>

      {/* Bulk edit button - Pro only */}
      {!bulkEditMode && hasApplications && (
        <>
          <div className="h-4 w-px bg-app-line" />
          <button
            onClick={isPro ? onToggleBulkEdit : onNavigateToPricing}
            disabled={!isPro}
            className="inline-flex items-center gap-1.5 border border-app-line bg-app-surface px-2.5 py-1 text-xs font-medium text-app-ink hover:bg-app-subtle hover:text-app-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-app-surface disabled:hover:text-app-ink rounded-lg"
          >
            <ArchiveBoxIcon className="h-3.5 w-3.5" />
            Bulk edit
          </button>
        </>
      )}

      {/* Column visibility button - only active in table view */}
      {displayMode === 'table' && (
        <div className="relative ml-auto" data-column-selector="true">
            <button
              onClick={() => setIsColumnSelectorOpen(!isColumnSelectorOpen)}
              title="Manage columns"
              aria-label="Manage columns"
              className="inline-flex items-center gap-1.5 border border-app-line bg-app-surface px-2.5 py-1 text-xs font-medium text-app-ink hover:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
            >
              <ViewColumnsIcon className="h-3.5 w-3.5" />
              Columns
            </button>
            {isColumnSelectorOpen && (
              <div className="absolute left-0 mt-2 w-56 border border-app-line bg-app-surface p-3 z-20 rounded-2xl shadow-app-card">
                <p className="text-xs font-semibold uppercase text-app-muted mb-2">Visible columns</p>
                <div className="space-y-2">
                  {(['favorite', 'company', 'status', 'salary', 'target', 'stages', 'note', 'createdAt', 'updatedAt'] as const).map((col) => {
                    const labels = {
                      favorite: 'Favorite',
                      company: 'Company',
                      status: 'Status',
                      salary: 'Salary',
                      target: 'Target',
                      stages: 'Progress',
                      note: 'Note',
                      createdAt: 'Added',
                      updatedAt: 'Updated'
                    }
                    const isVisible = settings.visibleColumns.includes(col)
                    return (
                      <label key={col} className="flex items-center gap-2 cursor-pointer hover:bg-app-subtle rounded px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => {
                            const newColumns = isVisible
                              ? settings.visibleColumns.filter(c => c !== col)
                              : [...settings.visibleColumns, col]
                            onSettingsChange({ ...settings, visibleColumns: newColumns })
                          }}
                          className="h-4 w-4 border-app-line bg-app-surface text-app-accent focus:ring-app-accent/50 rounded-lg"
                        />
                        <span className="text-sm text-app-ink">{labels[col]}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  )
}
